import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ConversationsProvider } from './contexts/ConversationsContext';
import ProtectedRoute from './components/ProtectedRoute';
import Sidebar from './components/Sidebar';
import SignIn from './components/SignIn';
import SignUp from './components/SignUp';
import VerifyEmail from './components/VerifyEmail';
import Subscribe from './components/Subscribe';
import BuyCredits from './components/BuyCredits';
import Chat from './components/Chat';
import Integrations from './components/Integrations';
import AdminPanel from './components/AdminPanel';

// Shared layout: Claude-style left sidebar + full-height content area
const SidebarLayout = ({ children }) => (
  <div className="flex h-screen overflow-hidden">
    <Sidebar />
    <main className="flex-1 min-w-0 h-full overflow-y-auto">
      {children}
    </main>
  </div>
);

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SettingsProvider>
          <ConversationsProvider>
          <Router>
            <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 text-gray-900 dark:from-gray-900 dark:to-black dark:text-white transition-colors">
            <Routes>
            {/* Public Routes */}
            <Route path="/signin" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
            {/* Target of the confirmation email — public by definition, since
                the whole point is that they aren't signed in yet. */}
            <Route path="/verify-email" element={<VerifyEmail />} />

            {/* Plans / paywall. Signed in but deliberately NOT inside
                SidebarLayout — a blocked user gets the plans screen, not a
                shell that suggests the app still works. */}
            <Route
              path="/subscribe"
              element={
                <ProtectedRoute>
                  <Subscribe />
                </ProtectedRoute>
              }
            />

            {/* Prepaid top-up. Outside SidebarLayout for the same reason as
                /subscribe: it's a payment screen, not part of the app shell. */}
            <Route
              path="/credits"
              element={
                <ProtectedRoute>
                  <BuyCredits />
                </ProtectedRoute>
              }
            />

            {/* Protected Routes */}
            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <SidebarLayout>
                    <Chat />
                  </SidebarLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/integrations"
              element={
                <ProtectedRoute>
                  <SidebarLayout>
                    <Integrations />
                  </SidebarLayout>
                </ProtectedRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute>
                  <SidebarLayout>
                    <AdminPanel />
                  </SidebarLayout>
                </ProtectedRoute>
              }
            />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/chat" replace />} />

            {/* 404 redirect */}
            <Route path="*" element={<Navigate to="/chat" replace />} />
            </Routes>
            </div>
          </Router>
          </ConversationsProvider>
        </SettingsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
